import {
  ApplicationExpression,
  Expression,
  StringConcatExpression,
  VariableExpression,
} from "@hypertune/sdk/src/shared";
import { VariableMap } from "@hypertune/shared-internal/src/expression/types";
import createApplication from "../../../../lib/expression/createApplication";
import {
  ExpressionControlContext,
  IncludeExpressionOptionFunction,
} from "../../../../lib/types";
import ExpressionControl from "./ExpressionControl";
import { liftPermissionsDeniedErrorMessage } from "../../../../lib/constants";
import isReadOnly from "../../../../lib/expression/isReadOnly";

export default function StringConcatExpressionControl({
  context,
  variables,
  setVariableName,
  expression,
  setExpression,
  includeExpressionOption,
}: {
  context: ExpressionControlContext;
  variables: VariableMap;
  setVariableName: { [variableId: string]: (newVariableName: string) => void };
  expression: StringConcatExpression;
  setExpression: (newExpression: Expression | null) => void;
  includeExpressionOption: IncludeExpressionOptionFunction;
}): React.ReactElement {
  return (
    <ExpressionControl
      context={{ ...context, disableStringListSplitAndWarnings: true }}
      variables={variables}
      setVariableName={setVariableName}
      valueTypeConstraint={{
        type: "ListValueTypeConstraint",
        itemValueTypeConstraint: { type: "StringValueTypeConstraint" },
      }}
      expression={expression.strings}
      setExpression={(newExpression: Expression | null): void =>
        setExpression({
          ...expression,
          strings: newExpression,
        })
      }
      lift={(child): void => {
        if (isReadOnly(context)) {
          // eslint-disable-next-line no-alert
          alert(liftPermissionsDeniedErrorMessage);
          return;
        }
        function replaceArgument(
          variable: VariableExpression | ApplicationExpression
        ): StringConcatExpression {
          const newExpression: StringConcatExpression = {
            ...expression,
            strings: child.replaceArgument(variable),
          };
          return newExpression;
        }
        const applicationExpression = createApplication({
          variables,
          rawArgument: child.argument,
          replacedVariableIdToNewVariable:
            child.replacedVariableIdToNewVariable,
          valueType: expression.valueType,
          replaceArgument,
          newVariableName: child.newVariableName,
          setExpressionEditorSelectedItem: (newSelectedItem) =>
            context.setExpressionEditorState({
              ...context.expressionEditorState,
              selectedItem: newSelectedItem,
            }),
        });
        setExpression(applicationExpression);
      }}
      parentExpression={expression}
      setParentExpression={setExpression}
      includeExpressionOption={includeExpressionOption}
    />
  );
}
